import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import config from "../config/config";
import Cookies from "js-cookie";
import axiosInstance from "../AxiosInstance";
import {
  clearSearchSelectionsHelper,
  clearJobCreateIdHelper,
} from "../util_functions/helpers";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [clientUser, setClientUser] = useState(null);
  const [workerUser, setWorkerUser] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [allWorkerUsers, setAllWorkerUsers] = useState([]);
  const [allClientUsers, setAllClientUsers] = useState([]);
  const [shouldFetchUser, setShouldFetchUser] = useState(true);

  const [unverifiedEmailWorkerUsers, setUnverifiedEmailWorkerUsers] = useState(
    []
  );
  const [unverifiedPhoneWorkerUsers, setUnverifiedPhoneWorkerUsers] = useState(
    []
  );
  const [profilePhotoReviewUsers, setProfilePhotoReviewUsers] = useState([]);
  const [bioReviewUsers, setBioReviewUsers] = useState([]);
  const [unverifiedWorkerEmailCount, setUnverifiedWorkerEmailCount] =
    useState(0);
  const [unverifiedWorkerPhoneCount, setUnverifiedWorkerPhoneCount] =
    useState(0);
  const [profilePhotoReviewCount, setProfilePhotoReviewCount] = useState(0);
  const [bioReviewCount, setBioReviewCount] = useState(0);
  const [totalUnverifiedWorkerCount, setTotalUnverifiedWorkerCount] =
    useState(0);

    const registerWorkerAccount = async (formData) => {
    try {
      const mobileForApi =
        formData.fullInternationalNumber;

      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: mobileForApi,
        password: formData.password,
        gender: formData.gender,
        referral: formData.referral,
        userType: formData.userType || "worker",
      };

      console.log("[UserContext] registerWorkerAccount payload →", payload);

      const response = await axiosInstance.post(
        `${config.API_BASE_URL}/v1/worker-users`,
        payload
      );

      const createdUser = response.data;
      console.log("[UserContext] Worker user created:", createdUser);

      setWorkerUser(createdUser);

      return createdUser;
    } catch (error) {
      console.error("[UserContext] Failed to create worker user:", error);
      throw error;
    }
  };


  const fetchAllWorkerUsers = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/worker-users`
      );
      setAllWorkerUsers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch all worker users:", error);
    }
  }, []);

  const fetchAllClientUsers = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/client-users`
      );
      setAllClientUsers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch all client users:", error);
      setAllClientUsers([]);
    }
  }, []);

  const fetchUserData = useCallback(async () => {
    try {
      const tokenResponse = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/auth/get-token`
      );
      const token = tokenResponse.data.token;

      if (token) {
        console.log("Token retrieved from API:", token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await axiosInstance.get(
          `${config.API_BASE_URL}/v1/auth/users`
        );
        const user = response.data;
        console.log("Context Logged In User data fetched:", user);
        if (user.userType === "client") {
          setClientUser(user);
        } else if (user.userType === "worker") {
          setWorkerUser(user);
        }
        return user;
      } else {
        console.error("Token not retrieved from API");
      }
    } catch (error) {
      console.error("Failed to fetch token and user data:", error);
      throw error;
    }
  }, []);

  const fetchLoggedInUser = useCallback(async () => {
    try {
      const sessionId = Cookies.get("sessionId");
      if (!sessionId) {
        console.log("No session ID found. Cannot fetch logged-in user.");
        return [];
      }

      console.log("Session ID found:", sessionId);
      const response = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/auth/users`,
        {
          headers: {
            sessionId: sessionId,
          },
        }
      );

      const userArray = response.data;
      if (Array.isArray(userArray) && userArray.length > 0) {
        console.log("Logged In User data (array):", userArray);
        const user = userArray[0];
        setLoggedInUser(user);
        return userArray;
      } else {
        console.log("No logged-in user found.");
        return [];
      }
    } catch (error) {
      console.error("Failed to fetch logged-in user by session ID:", error);
      return [];
    }
  }, []);

  const fetchUserDetailsById = useCallback(async (user) => {
    try {
      if (!user || !user.id || !user.userType) {
        console.error("Invalid user object provided");
        return;
      }

      const response = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/${user.userType}-users/${user.id}`
      );
      const userDetails = response.data;

      if (user.userType === "client") {
        setClientUser(userDetails);
      } else if (user.userType === "worker") {
        setWorkerUser(userDetails);
      }

      console.log("Fetched Context user details by ID:", userDetails);
    } catch (error) {
      console.error("Failed to fetch user details by ID:", error);
    }
  }, []);

  const fetchWorkerUserById = async (userId) => {
    try {
      const user = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/worker-users/${userId}`
      );
        setWorkerUser(user);
      console.log("Fetched worker user: ", user);
      return user;
    } catch (error) {
      console.error("Failed to fetch worker user :", error);
      return null;
    }
  };

  const fetchClientUserById = async (userId) => {
    try {
      const user = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/client-users/${userId}`
      );
        setClientUser(user);
      console.log("Fetched client user: ", user);
      return user;
    } catch (error) {
      console.error("Failed to fetch client user :", error);
      return null;
    }
  };

  const fetchWorkerUsersByApplicantIds = useCallback(async (applicantIds) => {
    try {
      const requests = applicantIds.map((id) =>
        axiosInstance.get(`${config.API_BASE_URL}/v1/worker-users/${id}`)
      );

      const responses = await Promise.all(requests);
      const workerUsers = responses.map((response) => response.data);
      return workerUsers;
    } catch (error) {
      console.error("Failed to fetch worker users by applicant IDs:", error);
      return [];
    }
  }, []);

  const fetchClientUserCustomerId = async (userId) => {
    try {
      const response = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/client-users/${userId}`
      );
      const customerUser = response.data;
      return customerUser;
    } catch (error) {
      console.error("Failed to fetch client customer user :", error);
      return null;
    }
  };

  const getUnverifiedWorkerUsersAndCounts = useCallback(async () => {
    try {
      console.log("Fetching worker users...");
      const response = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/worker-users`
      );
      const allWorkerUsers = response.data || [];
      console.log("Fetched worker users:", allWorkerUsers);

      const unverifiedEmailUsers = allWorkerUsers.filter(
        (user) => !user.isEmailVerified
      );
      console.log("Unverified email users:", unverifiedEmailUsers);

      const unverifiedPhoneUsers = allWorkerUsers.filter(
        (user) => !user.phoneNumberVerified
      );
      console.log("Unverified phone users:", unverifiedPhoneUsers);

      const profilePhotoReviewUsers = allWorkerUsers.filter(
        (user) => user.profilePhotoUpdated
      );
      console.log("Profile photo review users:", profilePhotoReviewUsers);

      const bioReviewUsers = allWorkerUsers.filter((user) => user.bioUpdated);
      console.log("Bio review users:", bioReviewUsers);

      const unverifiedWorkerEmailCount = unverifiedEmailUsers.length;
      const unverifiedWorkerPhoneCount = unverifiedPhoneUsers.length;
      const profilePhotoReviewCount = profilePhotoReviewUsers.length;
      const bioReviewCount = bioReviewUsers.length;
      const totalUnverifiedWorkerCount =
        unverifiedWorkerEmailCount +
        unverifiedWorkerPhoneCount +
        profilePhotoReviewCount +
        bioReviewCount;

      console.log("Unverified email count:", unverifiedWorkerEmailCount);
      console.log("Unverified phone count:", unverifiedWorkerPhoneCount);
      console.log("Profile photo review count:", profilePhotoReviewCount);
      console.log("Bio review count:", bioReviewCount);
      console.log("Total unverified count:", totalUnverifiedWorkerCount);

      setUnverifiedEmailWorkerUsers(unverifiedEmailUsers);
      setUnverifiedPhoneWorkerUsers(unverifiedPhoneUsers);
      setProfilePhotoReviewUsers(profilePhotoReviewUsers);
      setBioReviewUsers(bioReviewUsers);
      setUnverifiedWorkerEmailCount(unverifiedWorkerEmailCount);
      setUnverifiedWorkerPhoneCount(unverifiedWorkerPhoneCount);
      setProfilePhotoReviewCount(profilePhotoReviewCount);
      setBioReviewCount(bioReviewCount);
      setTotalUnverifiedWorkerCount(totalUnverifiedWorkerCount);
    } catch (error) {
      console.error("Failed to fetch worker users:", error);
      setUnverifiedEmailWorkerUsers([]);
      setUnverifiedPhoneWorkerUsers([]);
      setProfilePhotoReviewUsers([]);
      setBioReviewUsers([]);
      setUnverifiedWorkerEmailCount(0);
      setUnverifiedWorkerPhoneCount(0);
      setProfilePhotoReviewCount(0);
      setBioReviewCount(0);
      setTotalUnverifiedWorkerCount(0);
    }
  }, []);

  const getWorkerVerificationDetailsById = useCallback(async (userId) => {
    try {
      const response = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/worker-users/${userId}`
      );
      const user = response.data;

      if (user) {
        const verificationDetails = {
          isEmailUnverified: !user.isEmailVerified,
          isPhoneUnverified: !user.phoneNumberVerified,
          needsProfilePhotoReview: user.profilePhotoUpdated,
          needsBioReview: user.bioUpdated,
          emailUnverifiedCount: !user.isEmailVerified ? 1 : 0,
          phoneUnverifiedCount: !user.phoneNumberVerified ? 1 : 0,
          profilePhotoReviewCount: user.profilePhotoUpdated ? 1 : 0,
          bioReviewCount: user.bioUpdated ? 1 : 0,
          totalVerificationIssuesCount:
            (!user.isEmailVerified ? 1 : 0) +
            (!user.phoneNumberVerified ? 1 : 0) +
            (user.profilePhotoUpdated ? 1 : 0) +
            (user.bioUpdated ? 1 : 0),
        };

        console.log(
          "Worker Verification Details for User ID:",
          userId,
          verificationDetails
        );

        return verificationDetails;
      } else {
        console.log("No user found for User ID:", userId);
        return null;
      }
    } catch (error) {
      console.error(
        "Failed to fetch verification details for worker user:",
        error
      );
      return null;
    }
  }, []);

  const clearUserData = async () => {
    const getCookie = (name) => {
      const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
      return match ? match[2] : null;
    };

    const searchSelectionId = getCookie("searchSelectionId");
    if (searchSelectionId) {
      console.log(`Retrieved searchSelectionId: ${searchSelectionId}`);
    } else {
      console.warn("No searchSelectionId found before clearing cookies.");
    }

    const jobCreateId = getCookie("jobCreateId");
    if (jobCreateId) {
      console.log(`Retrieved jobCreateId: ${jobCreateId}`);
    } else {
      console.warn("No jobCreateId found before clearing cookies.");
    }

    setLoggedInUser(null);
    setClientUser(null);

    Cookies.remove("sessionId");
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["sessionId"];

    document.cookie.split(";").forEach((cookie) => {
      const cookieName = cookie.trim().split("=")[0];
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    if (searchSelectionId) {
      console.log(`Clearing search selections for ID: ${searchSelectionId}`);
      try {
        await clearSearchSelectionsHelper(searchSelectionId);
        console.log(
          `Successfully cleared search selections for ID: ${searchSelectionId}`
        );
      } catch (error) {
        console.error(
          `Failed to clear search selections for ID: ${searchSelectionId}`,
          error
        );
      }
    } else {
      console.warn(
        "No searchSelectionId found to clear after cookies were removed."
      );
    }

    if (jobCreateId) {
      console.log(`Clearing job data for ID: ${jobCreateId}`);
      try {
        await clearJobCreateIdHelper(jobCreateId);
        console.log(`Successfully cleared job data for ID: ${jobCreateId}`);
      } catch (error) {
        console.error(`Failed to clear job data for ID: ${jobCreateId}`, error);
      }
    } else {
      console.warn("No jobCreateId found to clear after cookies were removed.");
    }

    setShouldFetchUser(false);
    console.log("User data cleared successfully.");
  };

  const logout = async () => {
    try {
      await axiosInstance.post(`${config.API_BASE_URL}/v1/auth/logout`, {});

      clearUserData();
      console.log("User logged out successfully");
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  return (
    <UserContext.Provider
      value={{
        registerWorkerAccount,
        clientUser,
        workerUser,
        allWorkerUsers,
        allClientUsers,
        loggedInUser,
        unverifiedEmailWorkerUsers,
        unverifiedPhoneWorkerUsers,
        profilePhotoReviewUsers,
        bioReviewUsers,
        unverifiedWorkerEmailCount,
        unverifiedWorkerPhoneCount,
        profilePhotoReviewCount,
        bioReviewCount,
        totalUnverifiedWorkerCount,
        fetchLoggedInUser,
        setLoggedInUser,
        setClientUser,
        setWorkerUser,
        fetchUserData,
        fetchUserDetailsById,
        fetchWorkerUserById,
        fetchClientUserById,
        fetchWorkerUsersByApplicantIds,
        fetchClientUserCustomerId,
        fetchAllWorkerUsers,
        fetchAllClientUsers,
        getUnverifiedWorkerUsersAndCounts,
        getWorkerVerificationDetailsById,
        clearUserData,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
